import React, { useState, useEffect } from 'react';
import LoadingIndicator from '../components/common/LoadingIndicator';
import { 
  getAllServiceConfigs, 
  createServiceConfig, 
  updateServiceConfig,
  fetchServiceNames
} from '../services/serviceConfigService';
import '../styles/ServiceConfigPage.css';
import ParameterConfigPage from './ParameterConfigPage';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { useSession } from '../context/SessionContext';


import axios from '../services/axiosConfig';
const API_URL = process.env.REACT_APP_API_URL;

const ServiceConfigPage = () => {
  // Service Configuration states
  const [services, setServices] = useState([]);
  const [serviceForm, setServiceForm] = useState({
    id: '', // Added id property to the serviceForm state
    name: '',
    url: '',
    method: '',
    request: '',
    isActive: true
  });
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showPostRequest, setShowPostRequest] = useState(false);
  const [showServiceNameField, setShowServiceNameField] = useState(false);
  const [showKeySourcePathField, setShowKeySourcePathField] = useState(false);
  const [showRegexField, setShowRegexField] = useState(false);
  const [showIsActiveField, setShowIsActiveField] = useState(false);
  const [isNewParameter, setIsNewParameter] = useState(false); // State to track new parameter creation
   const { userId, userRoles, isAdmin, userName } = useSession();
 
  // Load service configs on component mount
  useEffect(() => {
    fetchServices();
    loadServiceNames();
  }, []);
  
  // Fetch services from API
  const fetchServices = async () => {
    try {
      setLoading(true);
      const data = await getAllServiceConfigs();
      setServices(data || []);
    } catch (err) {
      console.error("Error loading service configurations:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch service names from external API
  const loadServiceNames = async () => {
    try {
      const fetchedServices = await fetchServiceNames();

      // Ensure unique services by comparing IDs
      setServices(prevServices => {
        const existingIds = new Set(prevServices.map(service => service.id));
        const uniqueServices = fetchedServices.filter(service => !existingIds.has(service.id));
        return [...prevServices, ...uniqueServices];
      });
    } catch (err) {
      console.error("Error loading service names:", err);
    }
  };

  // Handle service form input changes
  const handleServiceFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setServiceForm({
      ...serviceForm,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Handle service method change
  const handleMethodChange = (e) => {
    const method = e.target.value;
    setServiceForm((prevForm) => ({
      ...prevForm,
      method
    }));

    // Show "Service Request" field only for "Post" method
    if (method === 'Post') {
      setShowPostRequest(true);
    } else {
      setShowPostRequest(false);
    }
  };
  
  // Add a function to save the service configuration
  const saveServiceConfig = async () => {
    try {
      const payload = {
        serviceName: serviceForm.name,
        url: serviceForm.url,
        method: serviceForm.method,
        request: serviceForm.request,
        userId: userId, // Use the userId from session context
      };

      const response = await axios.post(`${API_URL}/ServiceConfig/SaveService`, payload);

      if (response.data.success) {
        alert('Service saved successfully!');
        fetchServices(); // Refresh the services list
        setServiceForm({
          id: '', // Reset id on new save
          name: '',
          url: '',
          method: '',
          request: '',
          isActive: true,
        });
        setShowPostRequest(false);
      } else {
        alert(`Error: ${response.data.errorMessage}`);
      }
    } catch (error) {
      console.error('Error saving service:', error);
      alert('Failed to save service. Please try again.');
    }
  };

  // Handle save service
  const handleSaveService = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      
      // Validate required fields
      if (!serviceForm.name || !serviceForm.url || !serviceForm.method) {
        setError('Please fill in all required fields');
        setSaving(false);
        return;
      }
      
      // Validate post request if method is Post
      if (serviceForm.method === 'Post' && !serviceForm.request) {
        setError('Please enter a service request for POST method');
        setSaving(false);
        return;
      }
      
      const serviceData = { ...serviceForm };
      
      // If method is GET, clear request
      if (serviceData.method === 'Get') {
        serviceData.request = '';
      }
      
      // Find if service already exists by name
      const existingService = services.find(s => s.name === serviceData.name);
      
      let result;
      if (existingService) {
        result = await updateServiceConfig(existingService.id, serviceData);
      } else {
        result = await createServiceConfig(serviceData);
      }
      
      // Refresh services list
      await fetchServices();
      
      // Clear form
      setServiceForm({
          id: '', // Reset id on new save
        name: '',
        url: '',
        method: '',
        request: '',
        isActive: true
      });
      
      setShowPostRequest(false);
      
    } catch (err) {
      console.error("Error saving service:", err);
      setError('Failed to save service. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle service name selection change
  const handleServiceNameChange = (e) => {
    const selectedServiceName = e.target.value;
    const selectedService = services.find((service) => service.name === selectedServiceName);

    if (selectedService) {
      // Populate the form with the selected service details
      setServiceForm({
        id: selectedService.id || '', // Ensure id is set
        name: selectedService.name,
        url: selectedService.url || '',
        method: selectedService.method || '',
        request: selectedService.request || '',
        isActive: selectedService.isActive !== undefined ? selectedService.isActive : true,
      });

      // Update UI states based on the selected service
      setShowPostRequest(selectedService.method === 'Post');
      setShowIsActiveField(true);
    } else {
      // Clear the form if no service is selected
      setServiceForm({
        name: selectedServiceName,
        url: '',
        method: '',
        request: '',
        isActive: true,
      });

      setShowPostRequest(false);
      setShowIsActiveField(false);
    }
  };

  const fetchServiceDetails = async (serviceName) => {
    try {
      const response = await axios.get(`${API_URL}/ServiceConfig/GetServiceDetails?serviceName=${serviceName}`);

       if (Array.isArray(response.data) && response.data.length > 0) {
        const serviceDetails = response.data[0]; // Extract the first element from the array

        setServiceForm({
          id: serviceDetails.id || '',
          name: serviceDetails.name || '',
          url: serviceDetails.url || '',
          method: serviceDetails.method || '',
          request: serviceDetails.request || '',
          isActive: serviceDetails.isActive !== undefined ? serviceDetails.isActive : true,
        });

        // Update UI states based on the fetched service details
        setShowPostRequest(serviceDetails.method === 'Post');
        setShowIsActiveField(true);
      } else {
        console.error('No service details found for the selected service name.');
      }
    } catch (err) {
      console.error('Error fetching service details:', err);
    }
  };

  // Handle edit service
  const handleEditService = async () => {
    try {
      const payload = {
        serviceId: serviceForm.id ? serviceForm.id.toString() : '', // Add fallback for id
        url: serviceForm.url.trim(),
        method: serviceForm.method.trim(),
        request: serviceForm.request.trim(),
        isActive: serviceForm.isActive ? '1' : '0', // Convert boolean to string
        userId: userId, // Use the userId from session context
      };

      const response = await axios.put(`${API_URL}/ServiceConfig/EditService`, payload);

      if (response.data.success) {
        alert('Service updated successfully!');
       // fetchServices(); // Refresh the services list to reflect updated data
        setServiceForm({
          id: '', // Reset id on new save
          name: '',
          url: '',
          method: '',
          request: '',
          isActive: true,
        });
        setShowPostRequest(false);
        setShowIsActiveField(false);
      } else {
        alert(`Error: ${response.data.errorMessage}`);
      }
    } catch (error) {
      console.error('Error editing service:', error);
      alert('Failed to edit service. Please try again.');
    }
  };

  return (
    <div>
      {loading && <LoadingIndicator />}
  

      {/* Services Panel */}
      <div className=" elegant-aero">
      <span  className="span-title">Services</span>
       
        <div className="panel-content">
          <form onSubmit={(e) => {
            e.preventDefault();
            saveServiceConfig();
          }}>
            {/* Service Name */}
            <div className="form-group">
              <label htmlFor="serviceName">Service Name <span className="required-indicator">*</span></label>
              <CreatableSelect
                id="serviceName"
                name="name"
                className="service-name-select"
                value={serviceForm.name ? { value: serviceForm.name, label: serviceForm.name } : null}
                options={services.map((service) => ({ value: service.name, label: service.name }))}
                onChange={(selectedOption) => {
                  const value = selectedOption ? selectedOption.value : '';
                  // Always update the form immediately
                  const isExisting = services.some((service) => service.name === value);
                  if (isExisting) {
                    fetchServiceDetails(value); // Fetch and display parameter details
                  } else {
                    setServiceForm({
                      id: '',
                      name: value,
                      url: '',
                      method: '',
                      request: '',
                      isActive: true,
                    });
                  }
                }}
                onCreateOption={(inputValue) => {
                  // Set the new service name in the form but do not add it to the services list yet
                  setServiceForm((prevForm) => ({ ...prevForm, name: inputValue }));
                }}
                isSearchable
              />
            </div>

            {/* Service URL */}
            <div className="form-group">
              <label htmlFor="serviceUrl">Service URL <span className="required-indicator">*</span></label>
              <div className="input-container">
                <textarea 
                  id="serviceUrl"
                  name="url"
                  className="form-control"
                  value={serviceForm.url}
                  onChange={handleServiceFormChange}
                  required
                ></textarea>
              </div>
            </div>

            {/* Method */}
            <div className="form-group">
              <label htmlFor="method">Method <span className="required-indicator">*</span></label>
              <div className="input-container">
                <select 
                  id="method"
                  name="method"
                  className="form-control service-method-select"
                  value={serviceForm.method}
                  onChange={handleMethodChange}
                  required
                >
                  <option value="">Select</option>
                  <option value="Get">Get</option>
                  <option value="Post">Post</option>
                </select>
              </div>
            </div>

            {/* Post Request (conditional) */}
            {showPostRequest && (
              <div className="form-group">
                <label htmlFor="request">
                  Service Request <span className="required-indicator">*</span>
                </label>
                <div className="input-container">
                  <textarea
                    id="request"
                    name="request"
                    className="form-control"
                    value={serviceForm.request}
                    onChange={handleServiceFormChange}
                    required
                  ></textarea>
                </div>
              </div>
            )}

            {/* Is Active (always shown) */}
            <div className="form-group">
              <label htmlFor="isActive">Is Active</label>
              <div className="input-container">
                <select
                  id="isActive"
                  name="isActive"
                  className="form-control"
                  value={serviceForm.isActive ? 'True' : 'False'}
                  onChange={(e) =>
                    setServiceForm((prevForm) => ({
                      ...prevForm,
                      isActive: e.target.value === 'True',
                    }))
                  }
                >
                  <option value="True">True</option>
                  <option value="False">False</option>
                </select>
              </div>
            </div>

            {/* Save / Edit Button */}
            <div className="button-container panel-content">
              {services.some((service) => service.name === serviceForm.name) ? (
                <button
                  type="button"
                  className="save-button"
                  onClick={handleEditService} // Call the edit service function
                >
                  Edit
                </button>
              ) : (
                <button
                  type="submit"
                  className="save-button"
                  //disabled={saving || !serviceForm.name.trim()} // Ensure Save is enabled only if a valid name is provided
                >
                  Save
                </button>
              )}
            </div>

            {error && <div className="error-message">{error}</div>}
          </form>
        </div>
      </div>

      {/* Parameters Panel */}
      <ParameterConfigPage />
    </div>
  );
};

export default ServiceConfigPage;